import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, tap } from 'rxjs/operators';
import { of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LanguageEnum } from '../../shared/models/language.enum';
import * as actions from './layout.actions';
import { SessionService } from '../../../services/session.service';
import { ApiService } from '../services/api.service';
import { Optimus } from '@openng/optimus-ui/config';
import { cs } from '@openng/optimus-ui-locale/cs.json';
import { en } from '@openng/optimus-ui-locale/en.json';

@Injectable()
export class LayoutEffects {
    private actions$ = inject(Actions);
    private api = inject(ApiService);
    private translate = inject(TranslateService);
    private session = inject(SessionService);
    private optimusConfig = inject(Optimus);

    changeLanguage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(actions.changeLanguage),
                tap(({ language }) => {
                    const lang = language === 'cs' ? LanguageEnum.CZECH : language;
                    const primeLang = lang === 'cz' ? 'cs' : 'en';
                    this.translate.use(lang);
                    this.session.registerLanguage(lang);
                    this.optimusConfig.setTranslation(primeLang === 'cs' ? cs : en);
                }),
            ),
        { dispatch: false },
    );

    changeUserSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.changeUserSettings),
            switchMap(({ input }) =>
                this.api.updateUserSettings(input).pipe(
                    map(() => actions.changeUserSettingsSuccess()),
                    catchError((error) =>
                        of(actions.changeUserSettingsFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    getUserSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.getUserSettings),
            switchMap(() =>
                this.api.getUserSettings().pipe(
                    map((settings) =>
                        actions.getUserSettingsSuccess({ userSettings: settings }),
                    ),
                    catchError((error) => of(actions.getUserSettingsFailure({ error }))),
                ),
            ),
        ),
    );

    changeUserSettingsSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.changeUserSettingsSuccess),
            map(() => actions.getUserSettings()),
        ),
    );
}
